import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Smartphone, Share, PlusSquare, X, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { registerPortalServiceWorker, useInstallPrompt } from '../lib/pwa';

/**
 * Portal PWA glue: manifest + iOS meta tags, service worker registration and the
 * "Installer l'application" card (Chrome prompt or iOS manual steps).
 * portal: 'easer' | 'client'
 */
export function InstallAppCard({ portal, compact = false, headOnly = false }) {
  const { canPrompt, install, installed, dismissed, dismiss, needsIosHint } = useInstallPrompt();
  const label = portal === 'client' ? 'Espace client' : 'Espace Easer';

  useEffect(() => { registerPortalServiceWorker(); }, []);

  const head = (
    <Helmet>
      <link rel="manifest" href={`/manifest-${portal}.webmanifest`} />
      <link rel="apple-touch-icon" href="/icons/apple-touch-icon.png" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={label} />
      <meta name="theme-color" content="#2f5d4a" />
    </Helmet>
  );

  if (headOnly || installed || dismissed || (!canPrompt && !needsIosHint)) return head;

  const onInstall = async () => {
    const ok = await install();
    if (ok) toast.success(`${label} ajouté à votre écran d'accueil`);
  };

  return (
    <>
      {head}
      <Card className={`border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 ${compact ? 'p-3' : 'p-4'}`} data-testid="install-app-card">
        <div className="flex items-start gap-3">
          <span className="h-10 w-10 rounded-xl bg-[hsl(var(--primary))] text-white flex items-center justify-center flex-shrink-0"><Smartphone className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-base">Installer {label} sur votre téléphone</p>
            {needsIosHint ? (
              <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed" data-testid="install-app-ios-hint">
                Dans Safari, touchez <Share className="inline h-4 w-4 align-text-bottom" /> <strong>Partager</strong> puis <PlusSquare className="inline h-4 w-4 align-text-bottom" /> <strong>« Sur l'écran d'accueil »</strong>. L'application s'ouvrira ensuite comme une appli, même sans réseau.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground mt-0.5">Ouverture en un geste, plein écran, et accès même sans réseau.</p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {canPrompt && <Button size="sm" className="h-10" onClick={onInstall} data-testid="install-app-button"><Download className="h-4 w-4 mr-1" /> Installer l'application</Button>}
              <Button size="sm" variant="ghost" className="h-10" onClick={dismiss} data-testid="install-app-dismiss">Plus tard</Button>
            </div>
          </div>
          <button type="button" onClick={dismiss} aria-label="Fermer" className="p-1 text-muted-foreground hover:text-foreground" data-testid="install-app-close"><X className="h-4 w-4" /></button>
        </div>
      </Card>
    </>
  );
}
